import { useCallback, useEffect, useState } from "react";
import { api, API } from "@/lib/apiClient";
import { formatIST } from "@/lib/utils";
import { AdminLayout } from "@/components/AdminLayout";
import { DoctorPhoto } from "@/components/DoctorPhoto";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { INDIAN_STATES } from "@/lib/constants";
import { toast } from "sonner";
import {
  Download, Search, Flag, Trash2, RotateCcw, Loader2, ChevronLeft, ChevronRight,
} from "lucide-react";

const STATUS_COLORS = {
  active: "bg-green-100 text-green-700 border-green-200",
  flagged: "bg-amber-100 text-amber-700 border-amber-200",
  removed: "bg-red-100 text-red-700 border-red-200",
};

export default function AdminPledgesPage() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [state, setState] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [limit, setLimit] = useState(15);

  const loadTable = useCallback(() => {
    setLoading(true);
    api.get("/admin/pledges", { params: { search, state, status, page, limit } })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, state, status, page, limit]);

  useEffect(() => {
    const t = setTimeout(loadTable, 300);
    return () => clearTimeout(t);
  }, [loadTable]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/pledges/${id}`, { status: newStatus });
      toast.success(`Marked as ${newStatus}`);
      loadTable();
    } catch {
      toast.error("Update failed");
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const pageIds = data.items.map((p) => p.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently delete ${selected.size} selected pledge(s)? This also removes their certificates and cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await api.post("/admin/pledges/bulk-delete", { ids: [...selected] });
      toast.success(`Deleted ${res.data.deleted} pledge(s)`);
      setSelected(new Set());
      loadTable();
    } catch {
      toast.error("Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const exportXlsx = () => {
    const token = localStorage.getItem("hip_admin_token");
    fetch(`${API}/admin/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "healthy-india-pledges.xlsx";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Excel exported");
      })
      .catch(() => toast.error("Export failed"));
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <AdminLayout>
      <div className="rounded-2xl bg-white border border-slate-200 p-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="admin-search" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Search name, reg no, city, email…" className="pl-9" />
          </div>
          <Select value={state} onValueChange={(v) => { setPage(1); setState(v); }}>
            <SelectTrigger data-testid="filter-state" className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="all">All states</SelectItem>
              {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
            <SelectTrigger data-testid="filter-status" className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
              <SelectItem value="removed">Removed</SelectItem>
            </SelectContent>
          </Select>
          <Button
            data-testid="bulk-delete-btn"
            onClick={bulkDelete}
            disabled={selected.size === 0 || deleting}
            variant="outline"
            className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete selected{selected.size > 0 ? ` (${selected.size})` : ""}
          </Button>
          <Button data-testid="export-xlsx-btn" onClick={exportXlsx} variant="outline" className="rounded-full border-slate-300">
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
        </div>

        <div className="mt-5 overflow-x-auto">
          <Table data-testid="pledges-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox data-testid="select-all-checkbox" checked={allOnPageSelected} onCheckedChange={toggleAllOnPage} aria-label="Select all on page" />
                </TableHead>
                <TableHead>Photo</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Reg No</TableHead>
                <TableHead>Specialty</TableHead>
                <TableHead>City / State</TableHead>
                <TableHead>MR / Link</TableHead>
                <TableHead>Pledged At</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></TableCell></TableRow>
              ) : data.items.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-400">No pledges found</TableCell></TableRow>
              ) : (
                data.items.map((p) => (
                  <TableRow key={p.id} data-testid={`pledge-row-${p.id}`} className={selected.has(p.id) ? "bg-red-50/50" : undefined}>
                    <TableCell>
                      <Checkbox data-testid={`select-${p.id}`} checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} aria-label={`Select ${p.name}`} />
                    </TableCell>
                    <TableCell><DoctorPhoto pledge={p} /></TableCell>
                    <TableCell className="font-medium text-slate-900">{p.name}</TableCell>
                    <TableCell className="text-slate-600">{p.reg_no}</TableCell>
                    <TableCell className="text-slate-600">{p.specialty}</TableCell>
                    <TableCell className="text-slate-600">{p.city}, {p.state}</TableCell>
                    <TableCell className="text-slate-600 text-xs">
                      {p.source_link ? `Link: ${p.source_link}` : `${p.mr_name || "—"}${p.mr_emp_code ? ` (${p.mr_emp_code})` : ""}`}
                    </TableCell>
                    <TableCell className="text-slate-600 text-xs whitespace-nowrap">
                      {formatIST(p.created_at)}
                    </TableCell>
                    <TableCell><Badge className={`border ${STATUS_COLORS[p.status]} font-medium capitalize`} variant="outline">{p.status}</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      <a href={`${API}/certificate/${p.certificate_id}`} title="Certificate">
                        <Button size="icon" variant="ghost" className="h-8 w-8 inline-flex"><Download className="w-4 h-4 text-slate-500" /></Button>
                      </a>
                      {p.status !== "flagged" && (
                        <Button data-testid={`flag-btn-${p.id}`} onClick={() => updateStatus(p.id, "flagged")} size="icon" variant="ghost" className="h-8 w-8" title="Flag"><Flag className="w-4 h-4 text-amber-600" /></Button>
                      )}
                      {p.status !== "active" && (
                        <Button data-testid={`restore-btn-${p.id}`} onClick={() => updateStatus(p.id, "active")} size="icon" variant="ghost" className="h-8 w-8" title="Restore"><RotateCcw className="w-4 h-4 text-green-600" /></Button>
                      )}
                      {p.status !== "removed" && (
                        <Button data-testid={`remove-btn-${p.id}`} onClick={() => updateStatus(p.id, "removed")} size="icon" variant="ghost" className="h-8 w-8" title="Remove"><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <div className="flex items-center gap-3">
            <span>{data.total} total · Page {page} of {totalPages}</span>
            <Select value={String(limit)} onValueChange={(v) => { setPage(1); setLimit(Number(v)); }}>
              <SelectTrigger data-testid="page-size-select" className="w-[130px] h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[15, 25, 50, 100].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} per page</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button data-testid="prev-page" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} size="sm" variant="outline" className="rounded-full"><ChevronLeft className="w-4 h-4" /></Button>
            <Button data-testid="next-page" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} size="sm" variant="outline" className="rounded-full"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
